import { useMemo } from 'react';
import { makeCustomRange } from 'helpers';
import { DOTS } from 'modules/admin-panel/pages/results/utils';

interface IUsePaginationProps {
	totalPageCount?: number;
	currentPage: number;
	siblingCount: number;
	boundaryCount: number;
}

export const usePagination = ({
	totalPageCount,
	currentPage,
	siblingCount,
	boundaryCount,
}: IUsePaginationProps) => {
	return useMemo(() => {
		if (!totalPageCount) return [];

		const firstPageIndex = 1;
		const shownBoundaryPagesCount = boundaryCount * 2;
		const currentPageWithSiblingsCount = 2 * siblingCount + 1;
		const sideItemsCount = boundaryCount + currentPageWithSiblingsCount;

		if (
			shownBoundaryPagesCount + currentPageWithSiblingsCount >=
			totalPageCount - 2
		) {
			return makeCustomRange(firstPageIndex, totalPageCount);
		}

		const leftSiblingIndex = Math.max(
			currentPage - siblingCount,
			firstPageIndex,
		);
		const rightSiblingIndex = Math.min(
			currentPage + siblingCount,
			totalPageCount,
		);

		const shouldShowLeftDots = rightSiblingIndex - sideItemsCount > 1;
		const shouldShowRightDots =
			totalPageCount - leftSiblingIndex - sideItemsCount > 0;

		if (!shouldShowLeftDots && shouldShowRightDots) {
			const leftRange = makeCustomRange(firstPageIndex, sideItemsCount + 1);
			return [...leftRange, DOTS, totalPageCount];
		}

		if (shouldShowLeftDots && !shouldShowRightDots) {
			const rightRange = makeCustomRange(
				totalPageCount - sideItemsCount,
				totalPageCount,
			);
			return [firstPageIndex, DOTS, ...rightRange];
		}

		const middleRange = makeCustomRange(leftSiblingIndex, rightSiblingIndex);
		return [firstPageIndex, DOTS, ...middleRange, DOTS, totalPageCount];
	}, [totalPageCount, siblingCount, currentPage, boundaryCount]);
};
